import * as _ from 'lodash';
import { AbstractApiModel } from './abstract-api-model';
import { ApiLabel } from './api-label';
import { HasOrganization } from './interfaces/has-organization';
import { Map } from './map';
import { Organization } from './organization';
import { Play } from './play';
import { Robot } from './robot';

export class Store extends AbstractApiModel<Store> implements HasOrganization {
  name: string;
  ipAddress: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  smsAlertPhone: string;
  clientStoreId: string;
  map: Map;
  organizationId: number;
  aholdPaType: string;
  countryCode: string;
  timezone: string;
  solutions: string[];
  organization: Organization;
  robots: Robot[];
  captureOnly: boolean;
  autoAcceptConfidentHoles: boolean;
  saltConfiguration: any;
  latitude: number;
  longitude: number;
  pmInterval: number;
  lastPriceUpdate: Date;
  declare labels?: ApiLabel[];

  static readonly defaultPlayTypes: string[] = [Play.TYPE_INSPECT, Play.TYPE_INSIGHT, Play.TYPE_FOCUS];
  activeTaskCount: number;

  deserialize(json: any): this {
    this.name = json.name;
    this.clientStoreId = json.client_store_id;
    this.address = json.address;
    this.city = json.city;
    this.state = json.state;
    this.zipCode = json.zip_code;
    this.phone = json.phone;
    this.smsAlertPhone = json.sms_alert_phone;
    this.ipAddress = json.ip_address;
    this.map = new Map();
    this.organizationId = json.organization_id;
    this.aholdPaType = json.ahold_pa_type;
    this.countryCode = json.country_code;
    this.timezone = json.timezone;
    this.robots = [];
    this.solutions = json.solutions;
    this.captureOnly = json.capture_only;
    this.autoAcceptConfidentHoles = json.auto_accept_confident_holes;
    this.saltConfiguration = json.salt_config;
    this.latitude = json.latitude;
    this.longitude = json.longitude;
    this.pmInterval = json.pm_interval;
    this.lastPriceUpdate = json.last_price_update ? new Date(json.last_price_update) : null;
    if (Object.prototype.hasOwnProperty.call(json, 'labels')) {
      this.labels = json.labels;
    }
    return super.deserialize(json);
  }

  serialize(): any {
    return Object.assign(super.serialize(), {
      name: this.name,
      client_store_id: this.clientStoreId,
      address: this.address,
      city: this.city,
      state: this.state,
      zip_code: this.zipCode,
      phone: this.phone,
      sms_alert_phone: this.smsAlertPhone,
      ip_address: this.ipAddress,
      organization_id: this.organizationId,
      ahold_pa_type: this.aholdPaType,
      country_code: this.countryCode,
      timezone: this.timezone,
      solutions: this.solutions,
      capture_only: this.captureOnly,
      auto_accept_confident_holes: this.autoAcceptConfidentHoles,
      salt_config: this.saltConfiguration,
      pm_interval: this.pmInterval
    });
  }

  featureEnabled(featureName: string): boolean {
    if (_.isNil(this.robots)) {
      return false;
    }
    for (const robot of this.robots) {
      if (!_.isNil(robot.robotModel?.features) && robot.robotModel?.features.includes(featureName)) {
        return true;
      }
    }
    return false;
  }

  solutionEnabled(type: Solutions): boolean {
    return this.solutions ? this.solutions.includes(type) : false;
  }

  /**
   * Returns true when this store's pricing data is currently stale, i.e. the last price update is older
   * than `stalePriceThreshold` days ago. This reflects CURRENT staleness (as opposed to the historical
   * `hasStalePriceData` flag captured on a PlayExecution). Returns false when either the last update or
   * the threshold is missing.
   */
  isPriceStale(stalePriceThreshold: number): boolean {
    if (!this.lastPriceUpdate || stalePriceThreshold == null) {
      return false;
    }

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - stalePriceThreshold);

    return new Date(this.lastPriceUpdate) < cutoffDate;
  }

  hasLocationData(): boolean {
    return this.latitude !== null && this.longitude !== null;
  }

  playTypes(): string[] {
    const playTypes: string[] = _.clone(Store.defaultPlayTypes);
    if (this.featureEnabled('RFID')) {
      playTypes.push(Solutions.RFID);
    }
    if (this.solutionEnabled(Solutions.SECURITY_CHECKPOINT_WALK)) {
      playTypes.push(Play.TYPE_SECURITY_CHECKPOINT_WALK);
    }
    if (this.solutionEnabled(Solutions.SECURITY_PATROL)) {
      playTypes.push(Play.TYPE_SECURITY_PATROL);
    }
    if (this.solutionEnabled(Solutions.DELIVERY)) {
      playTypes.push(Play.TYPE_DELIVERY);
    }
    return playTypes;
  }
}

export enum Solutions {
  TOP_STOCK = 'top_stock',
  PRODUCT_IDENTIFICATION = 'product_identification',
  INSPECT = 'inspect',
  INSIGHT = 'insight',
  FOCUS = 'focus',
  RFID = 'rfid',
  SECURITY_CHECKPOINT_WALK = 'security_checkpoint_walk',
  SECURITY_PATROL = 'security_patrol',
  DELIVERY = 'delivery',
  PRODUCT_LOCATOR = 'product_locator'
}
